<template>
	<div class="row gx-lg-5 gx-md-0">
		<div class="col-lg-8 col-md-12">
			<div class="d-flex justify-content-between align-items-center">
				<h2>Report Builder</h2>
			</div>
			<div class="alert alert-warning" role="alert">
				<strong>Beta Version</strong> — Data Validation in Progress
			</div>
			<div class="row report-options mt-4 p-3">
				<fund-investments-selector v-model:fundsSelected="fundsSelected" v-model:investmentsSelected="investmentsSelected" v-model:periodSelected="periodSelected" v-model:yearsSelected="yearsSelected" v-model:quartersSelected="quartersSelected" v-model:monthsSelected="monthsSelected" v-model:fundsAvailable="fundsAvailable" v-model:investmentsAvailable="investmentsAvailable" ref="fundInvestmentsSelector" :include-date-range=true></fund-investments-selector>
				<div class="mt-4">
					<div class="form-label">* Data Inputs</div>
					<div class="row g-3">
						<div class="col-md-4" v-for="option in exportOptions" :key="option.value">
							<label class="form-label small mb-1">{{ option.label }}</label>
							<VueMultiselect
								:model-value="fieldSelections[option.value]"
								@update:model-value="value => setFields(option.value, value)"
								:multiple="true"
								:close-on-select="false"
								:searchable="true"
								:placeholder="fieldPlaceholder(option.value)"
								:options="fieldOptionsFor(option.value)"
								label="label"
								track-by="key">
									<template #noResult>No field matches that search.</template>
									<template #noOptions>No fields available.</template>
							</VueMultiselect>
						</div>
					</div>
					<small class="text-muted">Leave a dataset empty to exclude it from the report.</small>
				</div>
				<div v-if="!queryLimit.ok" class="alert alert-warning py-2 small mt-4 mb-0">
					{{ queryLimit.message }}
				</div>
				<div class="d-flex justify-content-between align-items-center mt-4">
					<p class="mb-0">* Indicates a required field</p>
					<router-link :to="{ name: 'ReportView', query: reportQuery }" custom v-slot="{ href }">
						<a :href="href" target="_blank" class="button btn btn-primary" :class="{ disabled: !canViewReport }" :aria-disabled="!canViewReport" :tabindex="canViewReport ? undefined : -1">View Report</a>
					</router-link>
				</div>

			</div>
			<div class="row" v-if="showSaved">
				<saved-reports></saved-reports>
			</div>

		</div>
		<div class="col-lg-4 col-md-12 d-md-none d-lg-block border-start" v-if="showSidebar">
			<report-sidebar></report-sidebar>
		</div>
	</div>
</template>

<script type="text/javascript">
	import FundInvestmentsSelector from "@/views/shared/FundInvestmentsSelector";
	import ReportSidebar from "./ReportSidebar.vue";
	import SavedReports from "./SavedReports.vue";
	import VueMultiselect from 'vue-multiselect'
	import { CustomExportApi } from "@/api/custom_export_api";
	import { checkReportQuery } from "@/api/report_limits";

	// Sentinel for the "All Fields" option. Never sent to the API — a dataset on
	// All Fields simply contributes no qualifiers, and absent already means all.
	const ALL_FIELDS = '__all__'

	export default {
		name: 'ReportsView',
		inheritAttrs: false,

		mixins: [],

		components: {
			FundInvestmentsSelector,
			ReportSidebar,
			SavedReports,
			VueMultiselect
		},

		filters: {
		},

		provide: function() {
			return {
			}
		},

		inject: [],

		props: [],

		data() {
			return {
				// showSidebar and showSaved control the visibility of saved reports and the sidebar
				// they're disabled for now per sc-22466, but can be toggled on here in the future
				showSidebar: false,
				showSaved: false,
				
				fundsSelected: [],
				investmentsSelected: [],
				periodSelected: null,
				yearsSelected: [],
				quartersSelected: [],
				monthsSelected: [],
				exportOptions: [
					{ label: 'Overview',             value: 'overview' },
					{ label: 'Fund Performance',     value: 'fundPerformance' },
					{ label: 'Financials',           value: 'financials' },
					{ label: 'Co-invest',            value: 'coinvest' },
					{ label: 'Lenders',              value: 'lenders' },
					{ label: 'Acquisitions',         value: 'acquisitions' },
					{ label: 'ESG',                  value: 'esg' },
					{ label: 'Control Sheet',        value: 'controlSheet' }
				],
				// exportKey -> selected option objects. Non-empty means the dataset is
				// in the report; the ALL sentinel means "every field".
				fieldSelections: {},
				fieldCatalog: {},
				fundsAvailable: 0,
				investmentsAvailable: 0,
				dataInputs: [
					'Gross MOIC',
					'Gross Profit', 
					'Company Updates',
					'Net MOIC',
					'Gross Margin', 
					'Co-invest/Lender Data',
					'Gross IRR',
					'Capex',
					'Financials/Accruals',
					'Net IRR',
					'Enterprise Value',
					'Add on Acquisitions',
					'EBITDA',
					'Bertram Funds Equity Invested',
					'ESG Data',
					'EBITDA Margin',
					'Company Overview',
					'ESG Scorecard'
				]
			}
		},

		computed: {
			// Selecting every fund and selecting none are the same request — the
			// backend treats an empty list as "no filter" — so both omit the
			// parameter. Enumerating them costs ~37 chars per GUID for nothing.
			fundParameters() {
				const chosen = this.fundsSelected
				if (chosen.length === 0) return undefined
				if (this.fundsAvailable > 0 && chosen.length >= this.fundsAvailable) return undefined
				return chosen.map(fund => fund.id).join(',')
			},
			// Same collapse. This is the big one: 42 GUIDs is ~1.6KB encoded.
			investmentParameters() {
				const chosen = this.investmentsSelected
				if (chosen.length === 0) return undefined
				if (this.investmentsAvailable > 0 && chosen.length >= this.investmentsAvailable) return undefined
				return chosen.map(company => company.guid).join(',')
			},
			exportParameters() {
				return this.selectedExports.length > 0 ? this.selectedExports.join(',') : 'overview'
			},
			yearsParameters() {
				return this.yearsSelected.length > 0 ? this.yearsSelected.join(',') : undefined
			},
			// Only the chosen exports' fields are offered — showing all ~148 when
			// one export is selected would bury the handful that are relevant.
			// The report URL carries the selection outright, so a link is portable:
			// it reproduces the report in any browser, for anyone with access.
			// Only set values are included; an omitted one means "all" everywhere
			// downstream, which is also what keeps a full selection from being
			// enumerated into the query string.
			reportQuery() {
				const q = {
					funds: this.fundParameters,
					investments: this.investmentParameters,
					exports: this.exportParameters,
					period: this.periodSelected || 'LTM',
					years: this.yearsParameters,
					quarters: this.quartersParameters,
					months: this.monthsParameters,
					fields: this.fieldsParameters
				}
				return Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== ''))
			},


			// Checked here because the server cannot: an over-long report URL is
			// refused by nginx before the page loads, so ExportRequestLimits never
			// runs. This is the only point that sees the URL before it exists.
			queryLimit() {
				return checkReportQuery(this.reportQuery)
			},

			canViewReport() {
				return this.selectedExports.length > 0 && !!this.periodSelected && this.queryLimit.ok
			},

			// A dataset is in the report when its dropdown has anything selected.
			// This replaced a separate tickbox, so inclusion and field choice are
			// now the same control.
			selectedExports() {
				return this.exportOptions
					.map(o => o.value)
					.filter(key => (this.fieldSelections[key] || []).length > 0)
			},

			// Emitted only for datasets narrowed to specific fields. A dataset on
			// ALL_FIELDS contributes nothing, because an absent qualifier already
			// means "every field" server-side — and enumerating all 148 would
			// overflow the request line.
			fieldsParameters() {
				const keys = []
				this.selectedExports.forEach(exportKey => {
					const chosen = this.fieldSelections[exportKey] || []
					if (chosen.some(f => f.all)) return
					const available = (this.fieldCatalog[exportKey] || []).length
					if (available > 0 && chosen.length >= available) return
					chosen.forEach(f => keys.push(f.key))
				})
				return keys.length > 0 ? keys.join(',') : undefined
			},

			quartersParameters() {
				return this.quartersSelected.length > 0 ? this.quartersSelected.join(',') : undefined
			},
			monthsParameters() {
				return this.monthsSelected.length > 0 ? this.monthsSelected.join(',') : undefined
			},
		},

		created() {
			// Seed a key per dataset so every dropdown has a bound array from the
			// first render rather than becoming reactive only once touched.
			this.fieldSelections = Object.fromEntries(this.exportOptions.map(o => [o.value, []]))
		},

		async mounted() {
			try {
				const meta = await CustomExportApi.getMeta()
				this.fieldCatalog = meta?.fields || {}
			} catch (err) {
				// The picker degrades to "All Fields" rather than blocking the report.
				console.error('Failed to load export field catalog:', err)
			}
		},


		methods: {
			// "All Fields" leads each list so the common choice is the first thing
			// you see. It carries `all: true` rather than being matched by label.
			fieldOptionsFor(exportKey) {
				const fields = [...(this.fieldCatalog[exportKey] || [])]
					.sort((a, b) => a.label.localeCompare(b.label))
				return [{ key: `${exportKey}.${ALL_FIELDS}`, label: 'All Fields', all: true }, ...fields]
			},

			// An empty dropdown excludes the dataset, so the placeholder must not
			// read "All Fields" — that would imply an empty control still returns
			// every column.
			fieldPlaceholder(exportKey) {
				return (this.fieldSelections[exportKey] || []).length > 0 ? '' : 'Not included'
			},

			// "All Fields" and specific fields are mutually exclusive: picking All
			// collapses the selection, and picking a field drops All. Without this
			// a selection could claim both at once.
			setFields(exportKey, value) {
				const previous = this.fieldSelections[exportKey] || []
				const hadAll = previous.some(f => f.all)
				const hasAll = (value || []).some(f => f.all)

				let next = value || []
				if (hasAll && !hadAll) {
					next = next.filter(f => f.all)
				} else if (hasAll && next.length > 1) {
					next = next.filter(f => !f.all)
				}
				this.fieldSelections = { ...this.fieldSelections, [exportKey]: next }
			}
		}
	}
</script>

<style lang="scss">
@import '../../styles/custom-variables';
@import '../../styles/equivate/checkboxes';
.report-options {
	background-color: $zebra;
}
.grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
}

.item {
    display: flex;
    align-items: center;
    position: relative;
}
</style>
